import PptxGenJS from "pptxgenjs"
import type { NumberFormatSpec, TableModel } from "utils/tableModel"
import { formatCell, toExcelNumFmt } from "utils/tableModel"
import { TOTALS_KEY } from "constants/tableConversion"
import {
  BarTile,
  ChartOptions,
  DashboardDocument,
  KpiTile,
  LineTile,
  PieTile,
  ScorecardTile,
  Tile,
} from "features/Dashboard/types/dashboardDocument"
import {
  leafLabel,
  modelCell,
  modelToBarSeries,
  modelToLineSeries,
  modelToPieSlices,
  omittedNote,
  shapeBarSeries,
  sortSlices,
} from "features/Dashboard/runtime/modelAdapters"
import {
  CHART_INK,
  chartUnits,
  colourLookup,
  entityColours,
  formatValue,
  hiddenColumns,
  stackEnds,
  TARGET_INK,
  tileEntities,
  valueDomain,
} from "features/Dashboard/components/Tiles/chartTheme"
import {
  panelGrid,
  panelLegendItems,
  panelOmitted,
  sharedAxisTop,
  showsPanelLegend,
} from "features/Dashboard/components/Tiles/panelCharts"
import { canRepeat, RepeatTile } from "features/Dashboard/runtime/panels"
import type { TileControlState } from "features/Dashboard/runtime/tileScript"
import {
  effectiveStatistic,
  kpiBaseText,
  viewStatistic,
} from "features/Dashboard/runtime/tileView"
import { ordinal, scorecardValues } from "features/Dashboard/runtime/scorecard"
import {
  inBand,
  layoutToSlideRect,
  pageBands,
  Rect,
} from "./layoutToSlideRect"
import type { ExportPanel } from "./collectExportTiles"

/** The 16:9 slide, in inches. */
const SLIDE = { w: 10, h: 5.625 }
/** Where a page's tiles go: under the slide title, above the footer. */
export const CONTENT: Rect = { x: 0.4, y: 0.9, w: 9.2, h: 4.25 }
const FONT = "Calibri"
/** Height of a tile title above the tile body, in inches. */
const TILE_TITLE_H = 0.3
/** The shortest tile that keeps its title: a title and as much body. */
const TITLED_TILE_MIN_H = 0.6
/**
 * A panel's category title over its chart, the legend line under the
 * panels, and the gap between panels, in inches.
 */
const PANEL_TITLE_H = 0.22
const PANEL_LEGEND_H = 0.25
const PANEL_GAP = 0.08
/**
 * Panel cells shorter than this drop the category title above the chart
 * (the chart names it in a small title of its own), and the gap between
 * panels narrows to `PANEL_TIGHT_GAP`, so the cell is all chart.
 */
const PANEL_TITLED_MIN_H = 0.52
const PANEL_TIGHT_GAP = 0.04
/**
 * The shortest panel drawn: when every row would be shorter, the rows that
 * do not fit are left out and a note under the panels says so.
 */
const PANEL_MIN_H = 0.3
/**
 * The narrowest panel of a single row: when the row's cells would be
 * narrower, the columns that do not fit are left out, with the same note.
 */
const PANEL_MIN_W = 0.3
/** The "Showing first N of M panels" line at the foot of the tile. */
const PANEL_NOTE_H = 0.2
/** Columns of a panel set whose tile names none. */
const DEFAULT_PANEL_COLUMNS = 3

/**
 * How a chart draws as one panel of a repeated tile: no legend of its own
 * (the panels share one line under the grid) and the value-axis end every
 * panel shares, in plotted units.
 */
interface PanelChart {
  axisMax?: number
  /**
   * The panel's category, as the chart's own small title, when its cell is
   * too short for a title above the chart: the title lives inside the
   * chart, so the panel stays named without growing.
   */
  title?: string
}

/** A panel's category as the chart's own title (see `PanelChart.title`). */
const panelTitleOptions = (panel?: PanelChart): PptxGenJS.IChartOpts =>
  panel?.title
    ? {
        showTitle: true,
        title: sanitizeText(panel.title),
        titleFontSize: 7,
        titleColor: INK,
        titleFontFace: FONT,
      }
    : {}

/** `#rrggbb` as the bare upper-case hex pptxgenjs takes. */
const hex = (colour: string): string => colour.replace("#", "").toUpperCase()

/** Inches to the thousandth, as the slide positions are written. */
const round3 = (n: number): number => Math.round(n * 1000) / 1000

/** C0 controls other than tab, newline and carriage return, and DEL. */
const xmlIllegal = (code: number): boolean =>
  code <= 0x08 ||
  code === 0x0b ||
  code === 0x0c ||
  (code >= 0x0e && code <= 0x1f) ||
  code === 0x7f

/**
 * `text` without the control characters XML refuses. pptxgenjs escapes
 * only `& < > " '`, so one of these in a title, label or cell would corrupt
 * the deck. Tab, newline and carriage return are kept.
 */
export const sanitizeText = (text: string): string =>
  Array.from(text)
    .filter((ch) => !xmlIllegal(ch.charCodeAt(0)))
    .join("")

const INK = hex(CHART_INK)
const MUTED = "6B7280"
const AXIS_TEXT = "4B5563"
const RULE = "E5E7EB"
const TABLE_BORDER = "D1D5DB"
const HEADER_FILL = "F3F4F6"
/** A change the good way and the bad way: the workspace's accent and error. */
const GOOD = "1BA8D1"
const BAD = "DC2626"

/**
 * The model's number format as an Excel/PowerPoint format code ("0.0%",
 * "#,##0", "0.00"); "General" when the model has none.
 */
export const numberFormatCode = (format: NumberFormatSpec | null): string =>
  toExcelNumFmt(format) ?? "General"

/** Markdown as plain text: no heading marks or emphasis, bullets as "•". */
const stripMd = (md: string): string =>
  md
    .replace(/^#+\s*/gm, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/^\s*[-*]\s+/gm, "• ")

/** A muted note centred in `rect`, where a tile has nothing to draw. */
const addNote = (slide: PptxGenJS.Slide, text: string, rect: Rect): void => {
  slide.addText(sanitizeText(text), {
    ...rect,
    fontSize: 10,
    color: MUTED,
    fontFace: FONT,
    align: "center",
    valign: "middle",
  })
}

/** Recessive axes and gridlines, text in ink, like the screen charts. */
const axisOptions = (): PptxGenJS.IChartOpts => ({
  catAxisLabelColor: AXIS_TEXT,
  catAxisLabelFontFace: FONT,
  catAxisLabelFontSize: 9,
  catAxisLineColor: RULE,
  catAxisMajorTickMark: "none",
  valAxisLabelColor: AXIS_TEXT,
  valAxisLabelFontFace: FONT,
  valAxisLabelFontSize: 9,
  valAxisLineShow: false,
  valAxisMajorTickMark: "none",
  valGridLine: { color: RULE, size: 0.75, style: "solid" },
})

/**
 * A legend for two or more series (a target line counts as one), under the
 * plot or right of it as the tile asks; one series needs none.
 */
const legendOptions = (
  series: number,
  legend: ChartOptions["legend"] = "bottom"
): PptxGenJS.IChartOpts => ({
  showLegend: series > 1 && legend !== "none",
  legendPos: legend === "right" ? "r" : "b",
  legendColor: INK,
  legendFontFace: FONT,
  legendFontSize: 9,
})

/** Value labels in ink, in the chart's number format, when the tile shows them. */
const valueLabelOptions = (
  chart: ChartOptions,
  code: string
): PptxGenJS.IChartOpts => ({
  showValue: chart.dataLabels === "on",
  dataLabelColor: INK,
  dataLabelFontFace: FONT,
  dataLabelFontSize: 8,
  dataLabelFormatCode: code,
})

/**
 * The value axis ends the tile fixes, in plotted units: its start (0 unless
 * a value is negative or the tile says otherwise) and its end when the tile
 * sets one. PowerPoint's own end already reaches a target line, which is a
 * series of the chart. A panel of a repeated tile ends at `sharedTop`, the
 * end every panel shares, or at the target when that lies above it.
 */
const axisRangeOptions = (
  values: (number | null)[],
  chart: ChartOptions,
  format: NumberFormatSpec | null,
  sharedTop?: number
): PptxGenJS.IChartOpts => {
  const { min, max } = valueDomain(values, chart, format, sharedTop)
  return {
    ...(typeof min === "number" ? { valAxisMinVal: min } : {}),
    ...(chart.axisMax !== undefined
      ? { valAxisMaxVal: chartUnits(chart.axisMax, format) }
      : sharedTop !== undefined && typeof max === "number"
        ? { valAxisMaxVal: max }
        : {}),
  }
}

/**
 * The footnote of a chart in `rect`, or null: `target` (always kept on a
 * tile, as its chart cannot draw it; on a panel only above a panel's least
 * height) and the note of left-out rows (only on a tile, and only when a
 * chart of `MIN_CHART_H` still fits above it), one after the other.
 */
const chartFootnote = (
  rect: Rect,
  notes: { target: string | null; omitted: string[] },
  panel: boolean
): string | null => {
  const room = rect.h - FOOTNOTE_H
  const target =
    notes.target !== null && (!panel || room >= PANEL_MIN_H)
      ? notes.target
      : null
  const omitted = !panel && room >= MIN_CHART_H ? omittedNote(notes.omitted) : null
  const text = [target, omitted].filter(Boolean).join("   ")
  return text || null
}

/**
 * `rect` less the footnote line when there is a footnote: on a tile never
 * under `MIN_CHART_H` (the chart then runs over the note rather than
 * shrink further), and never past `rect` either way.
 */
const chartRect = (rect: Rect, footnote: string | null, panel: boolean): Rect =>
  footnote === null
    ? rect
    : {
        ...rect,
        h: panel
          ? round3(rect.h - FOOTNOTE_H)
          : Math.max(Math.min(MIN_CHART_H, rect.h), rect.h - FOOTNOTE_H),
      }

/** The target's name and value as a slide shows it: "Target: 30.0%". */
const targetText = (
  target: NonNullable<ChartOptions["target"]>,
  format: NumberFormatSpec | null
): string =>
  `${target.label || "Target"}: ${formatValue(chartUnits(target.value, format), format)}`

/**
 * The target as a line series over `labels`: a dashed neutral line, no
 * markers, no value labels, drawn on the chart's own axes.
 */
const targetLine = (
  pptx: PptxGenJS,
  target: NonNullable<ChartOptions["target"]>,
  labels: string[],
  format: NumberFormatSpec | null
): PptxGenJS.IChartMulti => ({
  type: pptx.ChartType.line,
  data: [
    {
      name: sanitizeText(target.label || "Target"),
      labels,
      values: labels.map(() => chartUnits(target.value, format)),
    },
  ],
  options: {
    chartColors: [hex(TARGET_INK)],
    lineSize: 1.5,
    lineDash: "dash",
    lineDataSymbol: "none",
    showValue: false,
  },
})

/**
 * A combo chart. pptxgenjs takes a combo's options where a single chart
 * takes its data. Every part is drawn on the primary axes, so the chart
 * declares one category and one value axis and every part refers to them.
 */
const addCombo = (
  slide: PptxGenJS.Slide,
  parts: PptxGenJS.IChartMulti[],
  options: PptxGenJS.IChartOpts
): void => {
  ;(
    slide as unknown as {
      addChart: (t: PptxGenJS.IChartMulti[], o: PptxGenJS.IChartOpts) => void
    }
  ).addChart(parts, options)
}

/**
 * A footnote line at the foot of a chart's rect: a target the chart cannot
 * draw, and the rows the chart left out for being in another unit.
 */
const FOOTNOTE_H = 0.25
/** The shortest chart a tile keeps above a footnote, in inches. */
const MIN_CHART_H = 0.5
const addFootnote = (slide: PptxGenJS.Slide, text: string, rect: Rect): void => {
  slide.addText(sanitizeText(text), {
    x: rect.x,
    y: rect.y + rect.h - FOOTNOTE_H,
    w: rect.w,
    h: FOOTNOTE_H,
    fontSize: 9,
    color: MUTED,
    fontFace: FONT,
    align: "right",
    valign: "middle",
  })
}

/** Average Calibri character width, in ems, for estimating text width. */
const CHAR_EM = 0.5
/** The height of a line of text, in ems. */
const LINE_EM = 1.2
/** Table cell margins, in inches: left and right, top and bottom. */
const CELL_PAD_X = 0.05
const CELL_PAD_Y = 0.03
/** Table font sizes tried, largest first. */
const TABLE_FONT_SIZES = [10, 9, 8, 7, 6]
/** The row-label column's share of a table's width, at least and at most. */
const LABEL_SHARE_MIN = 0.3
const LABEL_SHARE_MAX = 0.45
/** The least font of a table that continues on further slides, in points. */
const CONTINUED_TABLE_MIN_PT = 8

/** Estimated width of `chars` characters at `pt` points, in inches. */
const textWidth = (chars: number, pt: number): number =>
  (chars * CHAR_EM * pt) / 72

const longestWord = (text: string): number =>
  Math.max(0, ...text.split(/\s+/).map((w) => w.length))

/**
 * Lines `text` takes in a cell `width` inches wide at `pt` points: words
 * wrap at spaces, a word longer than a line breaks across lines.
 */
const cellLines = (text: string, width: number, pt: number): number => {
  const perLine = Math.max(
    1,
    Math.floor((width - 2 * CELL_PAD_X) / textWidth(1, pt) + 1e-9)
  )
  return text.split("\n").reduce((total, para) => {
    let lines = 1
    let used = 0
    para
      .split(/\s+/)
      .filter(Boolean)
      .forEach((word) => {
        const need = used === 0 ? word.length : used + 1 + word.length
        if (need <= perLine) {
          used = need
        } else if (word.length <= perLine) {
          lines += used === 0 ? 0 : 1
          used = word.length
        } else {
          lines += (used === 0 ? 0 : 1) + Math.ceil(word.length / perLine) - 1
          used = word.length % perLine || perLine
        }
      })
    return total + lines
  }, 0)
}

/**
 * A table's column widths, in inches, summing to `w`: the row-label column
 * as wide as its longest label at `pt` points, between 30% and 45% of the
 * table, and the other `columns - 1` columns sharing the rest evenly.
 */
export const tableColumnWidths = (
  labels: string[],
  columns: number,
  w: number,
  pt: number
): number[] => {
  if (columns <= 1) return [round3(w)]
  const longest = Math.max(0, ...labels.map((l) => l.length))
  const need = textWidth(longest, pt) + 2 * CELL_PAD_X
  const share = Math.min(LABEL_SHARE_MAX, Math.max(LABEL_SHARE_MIN, need / w))
  const labelW = round3(w * share)
  const otherW = round3((w - labelW) / (columns - 1))
  return [labelW, ...Array.from({ length: columns - 1 }, () => otherW)]
}

/** One slide's share of a table: header plus body rows `first` on. */
export interface TablePart {
  /** The first body row (0 is the first row under the header). */
  first: number
  /** How many body rows. */
  count: number
  colW: number[]
  /** Row heights, header first, in inches. */
  rowH: number[]
  /** The table's height: the sum of `rowH`. */
  h: number
}

export interface TablePlan {
  fontSize: number
  /** The first part goes in the tile; each other on a slide of its own. */
  parts: TablePart[]
}

/**
 * How a table of `rows` (the header row first, the row label first in each
 * row) is drawn in a `box`, continuing on slides whose table area is
 * `more`. The font is the largest from 10 down to 6 pt at which every word
 * fits its column (no word breaks across lines) and the rows fit the box.
 * A table that does not fit at 6 pt continues anyway, so it keeps its
 * width font, 8 pt at least, and takes more slides. Rows that do not fit the box continue on as
 * many further parts as they need, each under the header again, laid out
 * for `more`'s width. A table that fits in one part fills its box.
 */
export const planTable = (
  rows: string[][],
  box: { w: number; h: number },
  more: { w: number; h: number }
): TablePlan => {
  const columns = rows[0]?.length ?? 1
  const labels = rows.slice(1).map((r) => r[0] ?? "")
  const body = rows.length - 1
  const widths = (w: number, pt: number) =>
    tableColumnWidths(labels, columns, w, pt)
  const heights = (colW: number[], pt: number) =>
    rows.map(
      (r) =>
        Math.max(1, ...r.map((t, c) => cellLines(t, colW[c], pt))) *
          ((LINE_EM * pt) / 72) +
        2 * CELL_PAD_Y
    )
  const sum = (ns: number[]) => ns.reduce((a, b) => a + b, 0)
  const wordsFit = (pt: number): boolean => {
    const colW = widths(box.w, pt)
    return rows.every((r) =>
      r.every(
        (t, c) => textWidth(longestWord(t), pt) + 2 * CELL_PAD_X <= colW[c] + 1e-9
      )
    )
  }
  const byWidth = TABLE_FONT_SIZES.find(wordsFit) ?? 6
  const fontSize =
    TABLE_FONT_SIZES.filter((pt) => pt <= byWidth).find(
      (pt) => sum(heights(widths(box.w, pt), pt)) <= box.h + 1e-9
    ) ?? Math.max(CONTINUED_TABLE_MIN_PT, byWidth)
  /** Body rows from `first` that fit under the header in `h`; one at least. */
  const take = (hs: number[], first: number, h: number): number => {
    let used = hs[0]
    let n = 0
    while (first + n < body && used + hs[1 + first + n] <= h + 1e-9) {
      used += hs[1 + first + n]
      n++
    }
    return Math.min(body - first, Math.max(1, n))
  }
  const part = (hs: number[], colW: number[], first: number, count: number) => {
    const rowH = [hs[0], ...hs.slice(1 + first, 1 + first + count)].map(round3)
    return { first, count, colW, rowH, h: round3(sum(rowH)) }
  }
  const colW = widths(box.w, fontSize)
  const hs = heights(colW, fontSize)
  const firstCount = take(hs, 0, box.h)
  if (firstCount >= body) {
    // Fits: the rows stretch, in proportion, to fill the box.
    const total = sum(hs)
    const stretch = total > 0 && total < box.h ? box.h / total : 1
    return {
      fontSize,
      parts: [part(hs.map((h) => h * stretch), colW, 0, body)],
    }
  }
  const parts = [part(hs, colW, 0, firstCount)]
  const moreW = widths(more.w, fontSize)
  const moreH = heights(moreW, fontSize)
  for (let first = firstCount; first < body; ) {
    const count = take(moreH, first, more.h)
    parts.push(part(moreH, moreW, first, count))
    first += count
  }
  return { fontSize, parts }
}

/**
 * A table tile as native tables: the first part in `rect`, and the rows
 * that do not fit it on further slides from `more`, each under the header
 * again, in the content area (see `planTable`).
 */
const addTable = (
  slide: PptxGenJS.Slide,
  model: TableModel,
  rect: Rect,
  more: () => PptxGenJS.Slide
): void => {
  const statDown = model.statAxis === "down"
  type Cell = { text: string; options?: PptxGenJS.TableCellProps }
  const head: Cell[] = [
    { text: "", options: { bold: true } },
    ...(statDown ? [{ text: "Statistic", options: { bold: true } }] : []),
    ...model.across.leaves.map((l) => ({
      text: sanitizeText(`${leafLabel(l)}${l.stat ? ` ${l.stat}` : ""}`),
      options: { bold: true, fill: { color: HEADER_FILL } },
    })),
  ]
  const rows: Cell[][] = [
    head,
    ...model.down.leaves.map((leaf, r) => [
      { text: sanitizeText(leafLabel(leaf)), options: { bold: leaf.isTotal } },
      // A measure row shows its unit ("pts", "mean") rather than its statistic.
      ...(statDown
        ? [{ text: sanitizeText(leaf.statLabel ?? leaf.stat ?? "") }]
        : []),
      ...model.cells[r].map((cell) => ({
        text: cell.role === "letters" ? "" : sanitizeText(formatCell(cell)),
        options: { align: "right" as const },
      })),
    ]),
  ]
  const plan = planTable(
    rows.map((r) => r.map((c) => c.text)),
    rect,
    CONTENT
  )
  plan.parts.forEach((part, i) => {
    const at = i === 0 ? rect : CONTENT
    ;(i === 0 ? slide : more()).addTable(
      [rows[0], ...rows.slice(1 + part.first, 1 + part.first + part.count)],
      {
        x: at.x,
        y: at.y,
        w: at.w,
        h: part.h,
        colW: part.colW,
        rowH: part.rowH,
        fontSize: plan.fontSize,
        fontFace: FONT,
        margin: [CELL_PAD_Y, CELL_PAD_X, CELL_PAD_Y, CELL_PAD_X],
        border: { type: "solid", pt: 0.5, color: TABLE_BORDER },
        autoPage: false,
      }
    )
  })
}

/**
 * A KPI tile: its number, its row (and column unless Totals), its base.
 * `model` is the tile's view, `raw` its last result (see `kpiBaseText`).
 */
const addKpi = (
  slide: PptxGenJS.Slide,
  tile: KpiTile,
  model: TableModel,
  raw: TableModel,
  rect: Rect,
  controls: TileControlState | undefined
): void => {
  const { row, column, decimals, showBase } = tile.visual
  const statistic = effectiveStatistic(tile, controls)
  const cell = modelCell(model, { row, column, statistic })
  if (!cell) {
    addNote(slide, "No value for this pick.", rect)
    return
  }
  // `decimals` overrides the cell's own places, as on screen.
  const value = sanitizeText(
    typeof cell.raw === "number" && decimals !== undefined
      ? formatValue(cell.raw, {
          decimals,
          thousands: true,
          percent: cell.text.endsWith("%"),
        })
      : cell.text
  )
  const label =
    cell.columnLabel !== TOTALS_KEY
      ? `${cell.rowLabel} · ${cell.columnLabel}`
      : cell.rowLabel
  const valueBox: Rect = { ...rect, h: rect.h * 0.6 }
  // As large as the box allows, 32 pt at most, so a long number stays inside.
  const fontSize = Math.max(
    12,
    Math.min(
      32,
      Math.floor(valueBox.h * 72 * 0.75),
      Math.floor((valueBox.w * 72) / (Math.max(1, value.length) * 0.6))
    )
  )
  slide.addText(value, {
    ...valueBox,
    fontSize,
    bold: true,
    color: INK,
    fontFace: FONT,
    align: "center",
    valign: "middle",
  })
  const baseText = showBase
    ? kpiBaseText(raw, model, { column, statistic }, cell.baseText)
    : null
  const base = baseText ? `Base ${baseText}` : ""
  slide.addText(sanitizeText([label, base].filter(Boolean).join("\n")), {
    x: rect.x,
    y: rect.y + valueBox.h,
    w: rect.w,
    h: rect.h - valueBox.h,
    fontSize: 10,
    color: MUTED,
    fontFace: FONT,
    align: "center",
    valign: "top",
  })
}

/**
 * A scorecard tile as three text boxes: its label (row, and column unless
 * Totals) above, its value, then the change (coloured by whether it is
 * the good way) with what it is against, the rank and the base. `model` is
 * the tile's view, `raw` its last result (see `kpiBaseText`).
 */
const addScorecard = (
  slide: PptxGenJS.Slide,
  tile: ScorecardTile,
  model: TableModel,
  raw: TableModel,
  rect: Rect,
  controls: TileControlState | undefined
): void => {
  const statistic = viewStatistic(tile, controls, raw)
  const card = scorecardValues(model, tile.visual, statistic)
  if (!card) {
    addNote(slide, "No value for this pick.", rect)
    return
  }
  const labelBox: Rect = { ...rect, h: rect.h * 0.22 }
  const valueBox: Rect = { ...rect, y: rect.y + labelBox.h, h: rect.h * 0.43 }
  const changeBox: Rect = {
    ...rect,
    y: valueBox.y + valueBox.h,
    h: rect.h - labelBox.h - valueBox.h,
  }
  slide.addText(
    sanitizeText(
      card.columnIsTotal ? card.label : `${card.label} · ${card.columnLabel}`
    ),
    {
      ...labelBox,
      fontSize: 10,
      color: MUTED,
      fontFace: FONT,
      valign: "bottom",
    }
  )
  const value = sanitizeText(card.text)
  // As large as the box allows, 32 pt at most, so a long number stays inside.
  const fontSize = Math.max(
    12,
    Math.min(
      32,
      Math.floor(valueBox.h * 72 * 0.75),
      Math.floor((valueBox.w * 72) / (Math.max(1, value.length) * 0.6))
    )
  )
  slide.addText(value, {
    ...valueBox,
    fontSize,
    bold: true,
    color: INK,
    fontFace: FONT,
    valign: "middle",
  })
  const base = kpiBaseText(
    raw,
    model,
    { column: card.columnId, statistic },
    null
  )
  const after = [
    card.rank ? `${ordinal(card.rank.position)} of ${card.rank.of}` : "",
    base ? `Base ${base}` : "",
  ]
    .filter(Boolean)
    .join("   ")
  const arrow =
    card.good === null || card.delta === null
      ? ""
      : card.delta > 0
        ? "▲ "
        : "▼ "
  const runs: PptxGenJS.TextProps[] = [
    ...(card.deltaText
      ? [
          {
            text: sanitizeText(`${arrow}${card.deltaText}`),
            options: {
              bold: true,
              color: card.good === null ? MUTED : card.good ? GOOD : BAD,
            },
          },
          {
            text: sanitizeText(` vs ${card.compareLabel}`),
            options: { color: MUTED, breakLine: Boolean(after) },
          },
        ]
      : []),
    ...(after ? [{ text: sanitizeText(after), options: { color: MUTED } }] : []),
  ]
  if (runs.length === 0) return
  slide.addText(runs, {
    ...changeBox,
    fontSize: 10,
    fontFace: FONT,
    valign: "top",
  })
}

/**
 * A bar tile as a native bar chart, its series shaped as on screen
 * (stacks scaled to 100%, categories sorted). A target is a dashed line
 * series on column charts; a horizontal bar chart cannot share its axes
 * with a line, so there the target is a footnote under the chart. Colours
 * are worked out from `raw`, the tile's last result, as on screen; a
 * single series gives each bar its own colour, its category's where the
 * dashboard's colours name it.
 */
const addBar = (
  pptx: PptxGenJS,
  slide: PptxGenJS.Slide,
  tile: BarTile,
  model: TableModel,
  raw: TableModel,
  rect: Rect,
  palette: string | undefined,
  colourMap: Record<string, string>,
  controls: TileControlState | undefined,
  panel?: PanelChart
): void => {
  const v = tile.visual
  const chart = tile.chart
  const s = shapeBarSeries(
    modelToBarSeries(model, {
      statistic: effectiveStatistic(tile, controls),
      seriesAxis: v.seriesAxis,
      showTotals: v.showTotals,
      hiddenCategories: hiddenColumns(v),
    }),
    { mode: v.mode, normalize: chart.normalize, sort: chart.sort }
  )
  if (s.categories.length === 0 || s.series.length === 0) {
    addNote(slide, "No values to chart.", rect)
    return
  }
  const entities = tileEntities(tile)
  const colourOf = entityColours(raw, v.seriesAxis, palette, colourMap, entities)
  const categoryColour = colourLookup(
    raw,
    v.seriesAxis === "across" ? "down" : "across",
    palette,
    colourMap,
    entities
  ).mappedOf
  const horizontal = v.orientation === "horizontal"
  const stacked = v.mode === "stacked"
  // PowerPoint lays horizontal categories out bottom-up; tables read top-down.
  const order = s.categories.map((_, i) => i)
  if (horizontal) order.reverse()
  const labels = order.map((i) => sanitizeText(s.categories[i].label))
  // A missing value stays null: pptxgenjs writes it as a blank point, so
  // the chart shows no bar rather than a zero one, as on screen.
  const data = s.series.map((ser) => ({
    name: sanitizeText(ser.label),
    labels,
    values: order.map((i) => ser.values[i]) as number[],
  }))
  const code = numberFormatCode(s.format)
  const target = chart.target
  const lineTarget = target !== undefined && !horizontal
  // A panel is never drawn past its cell (see `chartFootnote`).
  const footnote = chartFootnote(
    rect,
    {
      target:
        target !== undefined && horizontal
          ? targetText(target, s.format)
          : null,
      omitted: s.omitted,
    },
    Boolean(panel)
  )
  const plotted = stacked
    ? stackEnds(
        s.series.map((ser) => ser.values),
        s.categories.length
      )
    : s.series.flatMap((ser) => ser.values)
  // One series: one colour per bar (pptxgenjs writes a colour per point
  // when a single series has more than one), when a category has its own.
  const single = s.series.length === 1
  const seriesColour = colourOf(s.series[0].id)
  const pointColours = order.map(
    (i) => categoryColour(s.categories[i].id) ?? seriesColour
  )
  const chartColors =
    single && pointColours.some((c) => c !== seriesColour)
      ? pointColours.map(hex)
      : s.series.map((ser) => hex(colourOf(ser.id)))
  const barOptions: PptxGenJS.IChartOpts = {
    barDir: horizontal ? "bar" : "col",
    barGrouping: stacked ? "stacked" : "clustered",
    chartColors,
    ...valueLabelOptions(chart, code),
    // Stacked bars take their labels inside: outside the end corrupts them.
    dataLabelPosition: stacked ? "ctr" : "outEnd",
  }
  const options: PptxGenJS.IChartOpts = {
    ...chartRect(rect, footnote, Boolean(panel)),
    ...axisOptions(),
    ...panelTitleOptions(panel),
    ...legendOptions(
      data.length + (lineTarget ? 1 : 0),
      panel ? "none" : chart.legend
    ),
    ...axisRangeOptions(plotted, chart, s.format, panel?.axisMax),
    valAxisLabelFormatCode: code,
    dataLabelFormatCode: code,
  }
  if (lineTarget && target) {
    addCombo(
      slide,
      [
        { type: pptx.ChartType.bar, data, options: barOptions },
        targetLine(pptx, target, labels, s.format),
      ],
      options
    )
  } else {
    slide.addChart(pptx.ChartType.bar, data, { ...options, ...barOptions })
  }
  if (footnote) addFootnote(slide, footnote, rect)
}

/** A line tile as a native line chart; a target is a dashed line series. */
const addLine = (
  pptx: PptxGenJS,
  slide: PptxGenJS.Slide,
  tile: LineTile,
  model: TableModel,
  raw: TableModel,
  rect: Rect,
  palette: string | undefined,
  colourMap: Record<string, string>,
  controls: TileControlState | undefined,
  panel?: PanelChart
): void => {
  const v = tile.visual
  const chart = tile.chart
  const s = modelToLineSeries(model, {
    statistic: effectiveStatistic(tile, controls),
    hiddenSeries: v.hiddenSeries,
    waveVariable: v.waveVariable,
  })
  if (s.categories.length === 0 || s.series.length === 0) {
    addNote(slide, "No values to chart.", rect)
    return
  }
  const colourOf = entityColours(
    raw,
    "down",
    palette,
    colourMap,
    tileEntities(tile)
  )
  const labels = s.categories.map((c) => sanitizeText(c.label))
  // Missing values stay null (blank points) and the line breaks there.
  const data = s.series.map((ser) => ({
    name: sanitizeText(ser.label),
    labels,
    values: ser.values as number[],
  }))
  const code = numberFormatCode(s.format)
  const footnote = chartFootnote(
    rect,
    { target: null, omitted: s.omitted },
    Boolean(panel)
  )
  const lineOptions: PptxGenJS.IChartOpts = {
    chartColors: s.series.map((ser) => hex(colourOf(ser.id))),
    lineSize: 2,
    lineDataSymbol: v.showPoints ? "circle" : "none",
    lineDataSymbolSize: 7,
    // A ring in the surface colour keeps crossing points apart.
    lineDataSymbolLineColor: "FFFFFF",
    lineDataSymbolLineSize: 1.5,
    ...valueLabelOptions(chart, code),
    dataLabelPosition: "t",
  }
  const options: PptxGenJS.IChartOpts = {
    ...chartRect(rect, footnote, Boolean(panel)),
    ...axisOptions(),
    ...panelTitleOptions(panel),
    ...legendOptions(
      data.length + (chart.target ? 1 : 0),
      panel ? "none" : chart.legend
    ),
    ...axisRangeOptions(
      s.series.flatMap((ser) => ser.values),
      chart,
      s.format,
      panel?.axisMax
    ),
    displayBlanksAs: "gap",
    valAxisLabelFormatCode: code,
    dataLabelFormatCode: code,
  }
  if (chart.target) {
    addCombo(
      slide,
      [
        { type: pptx.ChartType.line, data, options: lineOptions },
        targetLine(pptx, chart.target, labels, s.format),
      ],
      options
    )
  } else {
    slide.addChart(pptx.ChartType.line, data, { ...options, ...lineOptions })
  }
  if (footnote) addFootnote(slide, footnote, rect)
}

/** A pie's legend and slice labels, in points. */
const PIE_TEXT_PT = 9
/**
 * A pie keeps a legend on the right only in a box at least this many times
 * as wide as it is tall; a narrower box has it under the pie.
 */
const PIE_RIGHT_LEGEND_ASPECT = 1.6
/** The share of the chart's width left to a legend on the right. */
const PIE_RIGHT_LEGEND_SHARE = 0.38
/**
 * PowerPoint wraps a data label wider than about a fifth of the chart, so
 * wider labels go inside the slices.
 */
const PIE_LABEL_MAX_SHARE = 0.2

/** Where a pie's legend, plot and slice labels go (see `pieFit`). */
export interface PieFit {
  /** Undefined without a legend. */
  legendPos?: "r" | "b"
  /**
   * The pie's plot area as fractions of the chart, with a legend on the
   * right: the left part of the chart, so the legend keeps the rest.
   */
  layout?: Rect
  dataLabelPosition: "outEnd" | "bestFit"
}

/**
 * How a pie fits `rect` with its `legend` and slice labels of `labelChars`
 * characters (0 for none). A legend on the right moves under the pie when
 * the box is narrow; kept on the right, the pie's plot area leaves it 38%
 * of the width, so every category shows. Labels go outside the slices only
 * while they fit there on one line, and the pie still has room; otherwise
 * PowerPoint places them (inside where they fit).
 */
export const pieFit = (
  rect: Rect,
  legend: PieTile["visual"]["legend"],
  labelChars: number
): PieFit => {
  const legendPos =
    legend === "none"
      ? undefined
      : legend === "right" && rect.w >= PIE_RIGHT_LEGEND_ASPECT * rect.h
        ? "r"
        : "b"
  const labelW = textWidth(labelChars, PIE_TEXT_PT) + 2 * CELL_PAD_X
  const labelH = (LINE_EM * PIE_TEXT_PT) / 72
  const plotW = legendPos === "r" ? rect.w * (1 - PIE_RIGHT_LEGEND_SHARE) : rect.w
  const pad = 0.05
  const diameter = (outside: boolean) =>
    Math.min(
      plotW - 2 * (outside ? labelW + pad : pad),
      rect.h - 2 * (outside ? labelH + pad : pad)
    )
  const outside =
    labelChars === 0 ||
    (labelW <= PIE_LABEL_MAX_SHARE * rect.w &&
      diameter(true) >= 0.6 * diameter(false))
  const d = Math.max(0.2, diameter(outside))
  return {
    legendPos,
    ...(legendPos === "r"
      ? {
          layout: {
            x: round3((plotW - d) / 2 / rect.w),
            y: round3((rect.h - d) / 2 / rect.h),
            w: round3(d / rect.w),
            h: round3(d / rect.h),
          },
        }
      : {}),
    dataLabelPosition: outside ? "outEnd" : "bestFit",
  }
}

const addPie = (
  pptx: PptxGenJS,
  slide: PptxGenJS.Slide,
  tile: PieTile,
  model: TableModel,
  raw: TableModel,
  rect: Rect,
  palette: string | undefined,
  colourMap: Record<string, string>,
  controls: TileControlState | undefined,
  panel?: PanelChart
): void => {
  const v = tile.visual
  const p = modelToPieSlices(model, {
    statistic: effectiveStatistic(tile, controls),
  })
  if (p.slices.length === 0) {
    addNote(slide, "No values to chart.", rect)
    return
  }
  const colourOf = entityColours(
    raw,
    "down",
    palette,
    colourMap,
    tileEntities(tile)
  )
  const slices = sortSlices(p.slices, tile.chart.sort)
  const data = [
    {
      name: sanitizeText(tile.title ?? "Share"),
      labels: slices.map((x) => sanitizeText(x.label)),
      values: slices.map((x) => x.value),
    },
  ]
  const footnote = chartFootnote(
    rect,
    { target: null, omitted: p.omitted },
    Boolean(panel)
  )
  const box = chartRect(rect, footnote, Boolean(panel))
  const showValue = tile.chart.dataLabels === "on"
  // Without a legend the slices carry their category names, as on screen.
  const showLabel = !panel && v.legend === "none"
  const longest = (texts: string[]) => Math.max(0, ...texts.map((t) => t.length))
  const valueChars = showValue
    ? longest(slices.map((x) => formatValue(x.value, p.format)))
    : 0
  const nameChars = showLabel ? longest(data[0].labels) : 0
  const fit = pieFit(
    box,
    panel ? "none" : v.legend,
    valueChars + nameChars + (valueChars && nameChars ? 2 : 0)
  )
  slide.addChart(v.donut ? pptx.ChartType.doughnut : pptx.ChartType.pie, data, {
    ...box,
    ...panelTitleOptions(panel),
    chartColors: slices.map((x) => hex(colourOf(x.id))),
    // A panel's slices are named once, in the legend under the panels.
    showLegend: !panel && v.legend !== "none",
    legendPos: fit.legendPos ?? "r",
    ...(fit.layout ? { layout: fit.layout } : {}),
    legendColor: INK,
    legendFontFace: FONT,
    legendFontSize: 9,
    holeSize: v.donut ? 60 : undefined,
    showValue,
    showLabel,
    // Outside the slices, value labels stay in ink whatever the slice
    // colour; labels that would wrap there go inside.
    dataLabelPosition: v.donut ? undefined : fit.dataLabelPosition,
    dataLabelColor: INK,
    dataLabelFontFace: FONT,
    dataLabelFontSize: 9,
    dataLabelFormatCode: numberFormatCode(p.format),
  })
  if (footnote) addFootnote(slide, footnote, rect)
}

/** To thousandths of an inch, up or down, forgiving float noise. */
const ceil3 = (n: number): number => Math.ceil(n * 1000 - 1e-6) / 1000
const floor3 = (n: number): number => Math.floor(n * 1000 + 1e-6) / 1000

/** `r` snapped to thousandths of an inch without growing past any edge. */
const snapInside = (r: Rect): Rect => {
  const x = ceil3(r.x)
  const y = ceil3(r.y)
  return {
    x,
    y,
    w: Math.max(0, round3(floor3(r.x + r.w) - x)),
    h: Math.max(0, round3(floor3(r.y + r.h) - y)),
  }
}

/** Where the parts of a repeated tile go inside its body, in inches. */
interface PanelLayout {
  /** One cell per panel drawn: the first `cells.length`, row by row. */
  cells: Rect[]
  /** Height of the category title at the top of each cell; 0 for none. */
  titleH: number
  /** The legend line under the panels, when the tile shows one. */
  legend: Rect | null
  /**
   * The note line at the foot: how many panels were left out, and the rows
   * the charts leave out for being in another unit.
   */
  note: Rect | null
}

const cellHeight = (gridH: number, rows: number, gap: number): number =>
  (gridH - gap * (rows - 1)) / rows

/**
 * The layout of `count` panels in `columns` columns inside `rect`, with the
 * legend line under them when `withLegend`, and a note line at the foot
 * when `withNote` (the charts leave rows out for their unit). Nothing lands
 * outside `rect` and no chart is taller than its cell. Short cells lose
 * their titles and the gaps narrow; when rows would still be under
 * `PANEL_MIN_H`, only the rows that fit are drawn and the note line says
 * how many. A single row does the same with its columns: when its cells
 * would be under `PANEL_MIN_W` wide, only the columns that fit are drawn.
 * A line that would leave no room for one row of charts is dropped.
 */
export const panelLayout = (
  rect: Rect,
  count: number,
  columns: number,
  withLegend: boolean,
  withNote = false
): PanelLayout => {
  const grid = panelGrid(count, columns)
  const legendH =
    withLegend && rect.h - PANEL_LEGEND_H >= PANEL_MIN_H ? PANEL_LEGEND_H : 0
  const fullH = rect.h - legendH
  const noteFits = fullH - PANEL_NOTE_H >= PANEL_MIN_H
  // A note asked for takes its line first, while a row of charts still fits.
  const askedH = withNote && noteFits ? PANEL_NOTE_H : 0
  const roomH = fullH - askedH
  const titled = cellHeight(roomH, grid.rows, PANEL_GAP) >= PANEL_TITLED_MIN_H
  const gap = titled ? PANEL_GAP : PANEL_TIGHT_GAP
  const tooShort =
    grid.rows > 1 && cellHeight(roomH, grid.rows, gap) < PANEL_MIN_H
  // A single row cannot lose rows: it keeps the columns that fit instead.
  const fitColumns = Math.max(
    1,
    Math.floor((rect.w + gap) / (PANEL_MIN_W + gap) + 1e-9)
  )
  const tooNarrow = grid.rows === 1 && fitColumns < grid.columns
  const cols = tooNarrow ? fitColumns : grid.columns
  const noteH =
    askedH > 0 || ((tooShort || tooNarrow) && noteFits) ? PANEL_NOTE_H : 0
  const gridH = fullH - noteH
  const rows = tooShort
    ? Math.max(1, Math.floor((gridH + gap) / (PANEL_MIN_H + gap) + 1e-9))
    : grid.rows
  const cellW = (rect.w - gap * (cols - 1)) / cols
  const cellH = cellHeight(gridH, rows, gap)
  const shown = Math.min(count, rows * cols)
  const cells = Array.from({ length: shown }, (_, i) =>
    snapInside({
      x: rect.x + (i % cols) * (cellW + gap),
      y: rect.y + Math.floor(i / cols) * (cellH + gap),
      w: cellW,
      h: cellH,
    })
  )
  const line = (y: number, h: number): Rect | null =>
    h > 0 ? snapInside({ x: rect.x, y, w: rect.w, h }) : null
  return {
    cells,
    titleH: titled ? PANEL_TITLE_H : 0,
    legend: line(rect.y + gridH, legendH),
    note:
      shown < count || askedH > 0
        ? line(rect.y + gridH + legendH, noteH)
        : null,
  }
}

/**
 * A repeated tile as a grid of native charts in `rect`, one per panel in
 * the tile's number of columns, each under its category's name. The
 * charts carry no legend of their own and share one value-axis end (see
 * `sharedAxisTop`); one line under the grid names the series (or slices)
 * in their colours, which follow the dashboard's colours as on screen.
 * The grid always fits the tile (see `panelLayout`): short panels have no
 * titles, and when panels would be too short to read only the rows that
 * fit are drawn, with a note saying how many.
 */
const addPanels = (
  pptx: PptxGenJS,
  slide: PptxGenJS.Slide,
  tile: RepeatTile,
  panels: ExportPanel[],
  rect: Rect,
  palette: string | undefined,
  colourMap: Record<string, string>,
  controls: TileControlState | undefined
): void => {
  const statistic = effectiveStatistic(tile, controls)
  const models = panels.map((p) => p.model)
  const items = panelLegendItems(
    tile,
    models[0],
    panels[0].rawModel ?? models[0],
    statistic,
    palette,
    colourMap
  )
  const omitted = omittedNote(panelOmitted(tile, models[0], statistic))
  const layout = panelLayout(
    rect,
    panels.length,
    tile.repeatBy?.columns ?? DEFAULT_PANEL_COLUMNS,
    showsPanelLegend(tile, items),
    omitted !== null
  )
  const panel: PanelChart = {
    axisMax: sharedAxisTop(tile, models, statistic),
  }
  layout.cells.forEach((cell, i) => {
    const p = panels[i]
    // A cell too short for a title above its chart names it in the chart.
    const look = [
      palette,
      colourMap,
      controls,
      layout.titleH > 0 ? panel : { ...panel, title: p.category },
    ] as const
    if (layout.titleH > 0) {
      slide.addText(sanitizeText(p.category), {
        ...cell,
        h: layout.titleH,
        fontSize: 9,
        bold: true,
        color: INK,
        fontFace: FONT,
        valign: "bottom",
      })
    }
    const body: Rect = {
      ...cell,
      y: round3(cell.y + layout.titleH),
      h: round3(cell.h - layout.titleH),
    }
    switch (tile.type) {
      case "bar":
        addBar(pptx, slide, tile, p.model, p.rawModel ?? p.model, body, ...look)
        break
      case "line":
        addLine(pptx, slide, tile, p.model, p.rawModel ?? p.model, body, ...look)
        break
      case "pie":
        addPie(pptx, slide, tile, p.model, p.rawModel ?? p.model, body, ...look)
        break
    }
  })
  if (layout.legend) {
    // One key per series: a square in its colour, then its name in ink.
    const runs: PptxGenJS.TextProps[] = items.flatMap((item, i) => [
      { text: "\u25A0 ", options: { color: hex(item.color) } },
      {
        text: sanitizeText(
          i < items.length - 1 ? `${item.label}    ` : item.label
        ),
        options: { color: INK },
      },
    ])
    slide.addText(runs, {
      ...layout.legend,
      fontSize: 9,
      fontFace: FONT,
      valign: "middle",
    })
  }
  if (layout.note) {
    const text = [
      layout.cells.length < panels.length
        ? `Showing first ${layout.cells.length} of ${panels.length} panels`
        : null,
      omitted,
    ]
      .filter(Boolean)
      .join("   ")
    slide.addText(sanitizeText(text), {
      ...layout.note,
      fontSize: 9,
      color: MUTED,
      fontFace: FONT,
      align: "right",
      valign: "middle",
    })
  }
}

/**
 * The PowerPoint deck for a dashboard export: each chosen page on 16:9
 * slides titled with the dashboard and page names, its tiles placed as on
 * the grid, and a footer with the filter summary and the date. A page
 * taller than a slide at full width is cut into bands between tile rows,
 * one slide each, numbered "(1/2)" and so on in the title (see
 * `pageBands`); a table whose rows do not fit its tile continues on slides
 * of its own, "<tile> (cont.)". Charts are
 * native PowerPoint charts (their data editable in PowerPoint), tables are
 * native tables, KPI, scorecard and text tiles are text boxes. A repeated
 * tile with panels in `panels` (their views, by tile id) is a grid of
 * charts in its place (see `addPanels`). Any other data tile without a
 * model in `models` (the tiles' views) is left out and its id returned in
 * `skipped`. `rawModels` holds the tiles' last results, for a KPI or
 * scorecard base the view no longer holds and for the charts' colours
 * (each falls back to the view). Charts and numbers draw the statistic the
 * viewer picked (`controls`), as on screen. `Pptx` replaces the pptxgenjs
 * constructor (tests pass a fake).
 */
export const buildPptxDeck = (args: {
  document: DashboardDocument
  pageIds: string[]
  models: Record<string, TableModel>
  panels?: Record<string, ExportPanel[]>
  rawModels?: Record<string, TableModel>
  controls?: Record<string, TileControlState | undefined>
  summary: string
  Pptx?: typeof PptxGenJS
}): { deck: PptxGenJS; skipped: string[] } => {
  const Ctor = args.Pptx ?? PptxGenJS
  const pptx = new Ctor()
  pptx.layout = "LAYOUT_16x9"
  pptx.title = sanitizeText(args.document.title)
  const palette = args.document.theme.chartPalette
  const colourMap = args.document.theme.categoryColours
  const skipped: string[] = []
  const footer = sanitizeText(
    [args.summary, new Date().toLocaleDateString()].filter(Boolean).join("   ")
  )
  /**
   * A tile in `rect` on `slide`; a table's rows that do not fit continue
   * on slides of their own, titled with the tile's (or the page's) name.
   */
  const addTile = (
    slide: PptxGenJS.Slide,
    tile: Tile,
    rect: Rect,
    pageTitle: string
  ): void => {
    if (tile.type === "text") {
      slide.addText(sanitizeText(stripMd(tile.markdown)), {
        ...rect,
        fontSize: 12,
        color: INK,
        fontFace: FONT,
        valign: "top",
      })
      return
    }
    if (tile.type === "image") {
      if (tile.src) {
        slide.addImage({
          data: tile.src,
          ...rect,
          sizing: { type: tile.fit, w: rect.w, h: rect.h },
        })
      }
      return
    }
    const panels = canRepeat(tile) ? (args.panels?.[tile.id] ?? []) : []
    const model = args.models[tile.id]
    if (!model && panels.length === 0) {
      skipped.push(tile.id)
      return
    }
    // A tile too short for its title and a body under it drops the
    // title, so nothing runs past the tile.
    const titled = Boolean(tile.title) && rect.h >= TITLED_TILE_MIN_H
    if (titled && tile.title) {
      slide.addText(sanitizeText(tile.title), {
        ...rect,
        h: TILE_TITLE_H,
        fontSize: 11,
        bold: true,
        color: INK,
        fontFace: FONT,
      })
    }
    const body: Rect = titled
      ? { ...rect, y: rect.y + TILE_TITLE_H, h: rect.h - TILE_TITLE_H }
      : rect
    const controls = args.controls?.[tile.id]
    if (panels.length > 0 && canRepeat(tile)) {
      addPanels(
        pptx,
        slide,
        tile,
        panels,
        body,
        palette,
        colourMap,
        controls
      )
      return
    }
    if (!model) return
    const raw = args.rawModels?.[tile.id] ?? model
    switch (tile.type) {
      case "table":
        addTable(slide, model, body, () =>
          addSlide(`${tile.title || pageTitle} (cont.)`)
        )
        break
      case "scorecard":
        addScorecard(slide, tile, model, raw, body, controls)
        break
      case "kpi":
        addKpi(slide, tile, model, raw, body, controls)
        break
      case "bar":
        addBar(pptx, slide, tile, model, raw, body, palette, colourMap, controls)
        break
      case "line":
        addLine(pptx, slide, tile, model, raw, body, palette, colourMap, controls)
        break
      case "pie":
        addPie(pptx, slide, tile, model, raw, body, palette, colourMap, controls)
        break
    }
  }
  /** A slide under `title`, with the footer. */
  const addSlide = (title: string): PptxGenJS.Slide => {
    const slide = pptx.addSlide()
    slide.addText(sanitizeText(title), {
      x: CONTENT.x,
      y: 0.25,
      w: CONTENT.w,
      h: 0.5,
      fontSize: 20,
      bold: true,
      color: INK,
      fontFace: FONT,
    })
    slide.addText(footer, {
      x: CONTENT.x,
      y: SLIDE.h - 0.4,
      w: CONTENT.w,
      h: 0.3,
      fontSize: 9,
      color: MUTED,
      fontFace: FONT,
    })
    return slide
  }
  args.document.pages
    .filter((p) => args.pageIds.includes(p.id))
    .forEach((page) => {
      const pageTitle = `${args.document.title}: ${page.title}`
      const bands = pageBands(
        page.tiles.map((t) => t.layout),
        CONTENT
      )
      if (bands.length === 0) {
        addSlide(pageTitle)
        return
      }
      bands.forEach((band, b) => {
        const slide = addSlide(
          bands.length > 1 ? `${pageTitle} (${b + 1}/${bands.length})` : pageTitle
        )
        page.tiles
          .filter((tile) => inBand(tile.layout, band))
          .forEach((tile) => {
            const rect = layoutToSlideRect(tile.layout, band, CONTENT)
            addTile(slide, tile, rect, page.title)
          })
      })
    })
  return { deck: pptx, skipped }
}
